<template>
  <SbPopover
    :target="targetEl"
    :active="tooltipVisible"
    :boundaries-element="intersectionElement"
    :placement="placement"
    :orientation="orientation"
    :offset="offset"
    :position-fixed="true"
  >
    <FadeTransition :duration="fadeDuration">
      <div
        v-if="tooltipVisible"
        class="tooltip"
        data-sb-regression="sb-tooltip"
        :class="[`tooltip--${variation}`]"
      >
        <!--
        @slot text, components, or HtmlElements as children of the component
        -->
        <slot />
      </div>
    </FadeTransition>
  </SbPopover>
</template>

<script>
import SbPopover from '@src/components/Popover/SbPopover.vue';
import FadeTransition from '@src/components/Transitions/Fade.vue';
/**
 * Tooltips identify an element when they are activated.
 * They may contain brief helper text about its function.
 * For example, they may contain aditional about a link text information.
 */
export default {
  name: 'SbTooltip',
  components: {
    SbPopover,
    FadeTransition,
  },
  props: {
    /**
     * Bounding context to fire the intersection callback on the tooltip.
     * Must of type Element.
     */
    intersectionElement: {
      required: false,
      default: null,
    },

    /**
     * Number indicating how long to wait before hiding the tooltip after a user has disabled it.
     * Units: time in `ms`
     */
    hideDelay: {
      type: Number,
      default: null,
    },
    /**
     * Number indicating how long to wait before hiding the tooltip after a user has disabled it.
     * Units: time in `ms`
     */
    showDelay: {
      type: Number,
      default: null,
    },
    /**
     * Positions the tooltip around the container element.
     * One of: `(top|bottom|left|right)`
     */
    orientation: {
      type: String,
      default: 'top',
      validator: (value) => value.match(/(top|bottom|left|right)/),
    },
    /**
     * Positions the tooltip with respect to its orientation.
     * This follows the same rules as flex-box
     * One of: `(start|center|end)`
     */
    placement: {
      type: String,
      default: 'start',
      validator: (value) => value.match(/(center|start|end)/),
    },
    /**
     * Offset from the container along orientation and placement axes.
     * To configure these, provide an object with the orientation and placement attributes.
     * values can be a percentage, px, or ems.
     */
    offset: {
      type: Object,
      default: () => ({
        orientation: '5px',
        placement: '',
      }),
    },
    /**
     * Style variation to give additional meaning.
     * `primary, secondary, tertiary, warning, minimal, caution`
     */
    variation: {
      type: String,
      default: 'primary',
      validator: (value) => value.match(/(primary|secondary)/),
    },
  },
  emits: [
    /**
     * Emitted when tooltip is open and content change is scheduled
     */
    'scheduleUpdate',
    /**
     * Emitted when tooltip is shown
     */
    'show',
    /**
     * Emitted when tooltip is hidden
     */
    'hide',
  ],
  data: () => ({
    tooltipVisible: false,
    targetEl: null,
    hideTimeout: null,
    showTimeout: null,
  }),
  computed: {
    toggleHideDelay() {
      return this.hideDelay || 250;
    },
    toggleShowDelay() {
      return this.showDelay || 250;
    },
    fadeDuration() {
      return 100;
    },
  },
  mounted() {
    this.targetEl = this.$el.parentElement;
    this.targetEl.addEventListener('mouseenter', this.showTooltip);
    this.targetEl.addEventListener('mouseleave', this.hideTooltip);
  },
  unmounted() {
    this.targetEl.removeEventListener('mouseenter', this.showTooltip);
    this.targetEl.removeEventListener('mouseleave', this.hideTooltip);
  },
  methods: {
    showTooltip() {
      clearTimeout(this.hideTimeout);
      this.showTimeout = setTimeout(() => {
        this.tooltipVisible = true;
        /**
         * Emitted when then tooltip should show
         * @param {bool} tooltipVisible
         */
        this.$emit('show', this.tooltipVisible);
      }, this.toggleShowDelay);
    },
    hideTooltip() {
      clearTimeout(this.showTimeout);
      this.hideTimeout = setTimeout(() => {
        this.tooltipVisible = false;
        /**
         * Emitted when then tooltip should hide
         * @param {bool} tooltipVisible
         */
        this.$emit('hide', this.tooltipVisible);
      }, this.toggleHideDelay);
    },
  },
};
</script>

<style lang="scss" scoped >
@import '@src/components/Tooltip/style/sb-tooltip.scss';
</style>
